import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import "./Mission.css";
import logo from "../assets/logo.jpg";
import SEOHead from "./SEOHead";

const MissionPage = () => {
  const [activeValue, setActiveValue] = useState(0);
    const navigate = useNavigate();
console.log(activeValue);
  const missionPillars = [
    {
      icon: '🏠',
      title: 'Community-First Housing',
      description: 'Finding a home matched to your culture, lifestyle, and values — not just your budget.',
      detail: 'Our Housing feature connects people to properties and roommates based on deep cultural compatibility. Price matters, but so does feeling understood where you live.'
    },
    {
      icon: '🍽️',
      title: 'Cultural Food Discovery',
      description: 'Helping people find restaurants, cafés, and markets that feel familiar and exciting.',
      detail: 'Food is culture. Our platform helps you discover authentic cuisines and local favourites that connect you to the cultures you love — wherever you are in the world.'
    },
    {
      icon: '👥',
      title: 'Genuine Community Connection',
      description: 'Building real connections between locals, movers, and expats through shared interests.',
      detail: 'Our Community feature lets you ask questions, share tips, and connect across topics like Housing, Food, Transit, Safety, Nightlife, Pets, and Events.'
    },
    {
      icon: '📰',
      title: 'Local Awareness & Feed',
      description: 'Keeping you informed about your city — news, events, and community stories that matter.',
      detail: 'The Feed brings you local and global news, community events (Food festivals, Tech meetups, Arts markets, Wellness), popular city guides, and social posts — all personalised.'
    }
  ];

  const impactAreas = [
    { category: 'International Movers', icon: '✈️', description: 'Helping people relocating abroad find culturally-matched housing, food, and community before they even arrive.', stats: 'From visa to belonging, every step covered' },
    { category: 'Graduate Students', icon: '🎓', description: 'Connecting students with academic-friendly housing and study-compatible roommates in university cities worldwide.', stats: 'Study-focused communities, matched' },
    { category: 'Remote Workers', icon: '💻', description: 'Finding digital nomads and remote workers peaceful, well-connected living environments with cultural amenities nearby.', stats: 'Work, explore, belong — anywhere' },
    { category: 'Local Explorers', icon: '🏘️', description: 'Helping residents discover hidden cultural gems, community events, and local stories in their own neighbourhoods.', stats: 'Your city, rediscovered' },
    { category: 'Families', icon: '👨‍👩‍👧‍👦', description: 'Finding family-friendly neighbourhoods with the right schools, parks, cultural spaces, and community values.', stats: 'Homes where families truly belong' },
    { category: 'Everyone', icon: '🌍', description: 'Whether you are moving across the world or just discovering your own city, ComLinkr connects you to belonging.', stats: 'Universal. Inclusive. For everyone.' }
  ];

  const values = [
    {
      title: 'Authenticity',
      icon: '💎',
      description: 'We believe in genuine connections and authentic community experiences.'
    },
    {
      title: 'Inclusivity',
      icon: '🌈',
      description: 'Every culture, lifestyle, and background deserves a place to call home.'
    },
    {
      title: 'Innovation',
      icon: '🚀',
      description: 'We continuously evolve our platform to better serve our community\'s needs.'
    },
    {
      title: 'Trust',
      icon: '🔒',
      description: 'Safety and trustworthiness are fundamental to creating lasting communities.'
    },
    {
      title: 'Empowerment',
      icon: '💪',
      description: 'We empower people to make informed decisions about their living situations.'
    },
    {
      title: 'Connection',
      icon: '🔗',
      description: 'We facilitate meaningful relationships that extend beyond roommate arrangements.'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveValue(prev => (prev + 1) % values.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [values.length]);

  return (
    <div className="mission-container">
      <SEOHead
        title="Our Mission — ComLinkr | Bridging People to Belonging"
        description="ComLinkr's mission is to transform how people discover and connect to their ideal living environments by prioritizing community, culture, and compatibility."
        canonical="https://www.comlinkr.com/mission"
      />
    <button className="mission-back-btn" onClick={() => navigate('/')}>← Back to Home</button>


      {/* Hero Section */}
      <section className="mission-hero">
        <div className="hero-background">
          <motion.div className="floating-mission-elements">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="floating-mission-element"
                animate={{
                  y: [0, -30, 0],
                  x: [0, 15, 0],
                  rotate: [0, 360]
                }}
                transition={{
                  duration: 4 + i,
                  repeat: Infinity,
                  delay: i * 0.3
                }}
                style={{
                  left: `${10 + i * 12}%`,
                  top: `${20 + (i % 4) * 20}%`
                }}
              >
                {missionPillars[i % 4]?.icon}
              </motion.div>
            ))}
          </motion.div>
        </div>

        <div className="container text-center">
          <motion.img 
            src={logo} 
            alt="ComLinkr Logo" 
            className="mission-logo"
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 1.5, type: "spring" }}
          />
          
          <motion.h1 
            className="mission-title"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.5, duration: 1 }}
          >
            Our <span className="mission-highlight">Mission</span>
          </motion.h1>
          
          <motion.p 
            className="mission-subtitle"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8, duration: 1 }}
          >
            Bridging people to belonging, not just places
          </motion.p>
          
          <motion.div 
            className="mission-statement"
            initial={{ y: 30, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.1, duration: 1 }}
          >
            <div className="statement-card">
              <h3>We believe everyone deserves to find where they belong</h3>
              <p>
                ComLinkr's mission is to transform how people discover and connect to their ideal living 
                environments by prioritizing community, culture, and compatibility over mere accommodation.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Mission Pillars */}
      <section className="mission-pillars-section">
        <div className="container">
          <motion.h2 
            className="section-title text-center mb-5"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            Our Mission Pillars
          </motion.h2>
          
          <div className="row">
            {missionPillars.map((pillar, index) => (
              <div key={index} className="col-lg-6 mb-4">
                <motion.div 
                  className="pillar-card"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.2 }}
                  whileHover={{ scale: 1.02, y: -5 }}
                >
                  <div className="pillar-icon">{pillar.icon}</div>
                  <div className="pillar-content">
                    <h4>{pillar.title}</h4>
                    <p className="pillar-description">{pillar.description}</p>
                    <p className="pillar-detail">{pillar.detail}</p>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="impact-areas-section">
        <div className="container">
          <motion.h2 
            className="section-title text-center mb-5"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            Who We Serve
          </motion.h2>
          
          <div className="row">
            {impactAreas.map((area, index) => (
              <div key={index} className="col-lg-4 col-md-6 mb-4">
                <motion.div 
                  className="impact-card"
                  initial={{ y: 50, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ 
                    scale: 1.05,
                    boxShadow: "0 20px 40px rgba(0,0,0,0.15)"
                  }}
                >
                  <div className="impact-icon">{area.icon}</div>
                  <h5>{area.category}</h5>
                  <p>{area.description}</p>
                  <div className="impact-stats">{area.stats}</div>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Values
      <section className="values-section">
        <div className="container">
          <motion.h2 
            className="section-title text-center mb-5"
            initial={{ y: 50, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true }}
          >
            Our Core Values
          </motion.h2>
          
          <div className="values-showcase">
            <div className="row">
              <div className="col-lg-8">
                <div className="active-value-display">
                  <motion.div 
                    key={activeValue}
                    className="value-spotlight"
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5 }}
                  >
                    <div className="value-spotlight-icon">{values[activeValue].icon}</div>
                    <h3>{values[activeValue].title}</h3>
                    <p>{values[activeValue].description}</p>
                  </motion.div>
                </div>
              </div>
              
              <div className="col-lg-4">
                <div className="values-list">
                  {values.map((value, index) => (
                    <motion.div 
                      key={index}
                      className={`value-item ${activeValue === index ? 'active' : ''}`}
                      onClick={() => setActiveValue(index)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className="value-icon">{value.icon}</span>
                      <span className="value-name">{value.title}</span>
                    </motion.div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section> */}

      {/* Mission Statement Detail */}
      <section className="mission-detail-section">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <motion.div 
                className="mission-detail-content"
                initial={{ x: -50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <h2>Why This Mission Matters</h2>
                <p className="lead">
                  In an increasingly connected yet fragmented world, finding genuine community 
                  has become more important than ever.
                </p>
                <div className="mission-points">
                  <div className="mission-point">
                    <span className="point-icon">🏠</span>
                    <div>
                      <strong>Housing Matched to You</strong>
                      <p>Properties and roommates filtered by culture, lifestyle, and values — not just price.</p>
                    </div>
                  </div>
                  <div className="mission-point">
                    <span className="point-icon">🍽️</span>
                    <div>
                      <strong>Food That Feels Familiar</strong>
                      <p>Discover cuisines and restaurants matched to your background and preferences.</p>
                    </div>
                  </div>
                  <div className="mission-point">
                    <span className="point-icon">👥</span>
                    <div>
                      <strong>Community at Your Fingertips</strong>
                      <p>Connect with locals, expats, and neighbours across topics that matter in daily life.</p>
                    </div>
                  </div>
                  <div className="mission-point">
                    <span className="point-icon">📰</span>
                    <div>
                      <strong>Stay Informed, Stay Connected</strong>
                      <p>Local news, events, city guides, and social posts — personalised to your location and interests.</p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
            
            {/* <div className="col-lg-6">
              <motion.div 
                className="mission-visual"
                initial={{ x: 50, opacity: 0 }}
                whileInView={{ x: 0, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="mission-stats-grid">
                  <div className="stat-item">
                    <div className="stat-number">87%</div>
                    <div className="stat-label">Feel more connected</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">92%</div>
                    <div className="stat-label">Find cultural compatibility</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">95%</div>
                    <div className="stat-label">Successful long-term matches</div>
                  </div>
                  <div className="stat-item">
                    <div className="stat-number">98%</div>
                    <div className="stat-label">Feel at home faster</div>
                  </div>
                </div>
              </motion.div>
            </div> */}
          </div>
        </div>
      </section>

      {/* Light CTA */}
      <section className="mission-light-cta">
        <div className="container text-center">
          <motion.div initial={{ y: 30, opacity: 0 }} whileInView={{ y: 0, opacity: 1 }} viewport={{ once: true }}>
            <span className="mission-eyebrow">Get Started</span>
            <h2 className="mission-cta-title">Ready to find where you belong?</h2>
            <p className="mission-cta-sub">Join early access and be the first to experience ComLinkr.</p>
            <button onClick={() => navigate('/')} className="mission-cta-btn">
              Get Early Access →
            </button>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default MissionPage;